/**
 * V1 ANOVA 計算過程: walkthrough 全文 + 事後検定ペア表
 */

import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ANOVAResult } from '../../../../utils/statistics/anova';
import type { AnalysisOptions, DataRow } from '../../../../stores/systems/keisuke/dashboardStore';
import {
  buildGroupValueMapFromGrid,
  buildOneWayAnovaWalkthrough,
  type OneWayAnovaMethodKind,
} from '../../../../utils/keisukeAnovaWalkthrough';
import { pValueToSymbol } from './keisukeDashboardChartUtils';
import { formatPValueCell } from './keisukeDashboardStatLabels';

export interface AnovaPairwiseRow {
  groupA: string;
  groupB: string;
  pValue: number;
  significant: boolean;
}

interface AnovaCalculationDetailsProps {
  rawResult: unknown;
  analysisOptions: AnalysisOptions;
  gridData: DataRow[];
  anovaPairwise?: AnovaPairwiseRow[];
  compact?: boolean;
}

export default function AnovaCalculationDetails({
  rawResult,
  analysisOptions,
  gridData,
  anovaPairwise,
  compact = false,
}: AnovaCalculationDetailsProps) {
  const { t } = useTranslation();
  const Title = compact ? 'h6' : 'h5';

  if (!rawResult || typeof rawResult !== 'object') return null;
  const r = rawResult as Record<string, unknown>;

  if (r.kruskalWallis) {
    return null;
  }

  const mapping = analysisOptions.mapping ?? {};
  const groupCol = mapping.groupColumn ?? mapping.factorAColumn ?? '';
  const valueCol = mapping.valueColumn ?? '';
  const postHoc = String(analysisOptions.postHoc ?? '');
  const anovaType = String(analysisOptions.anovaType ?? 'oneWay');

  if (r.groups && Array.isArray(r.groups) && groupCol && valueCol) {
    const result = r as unknown as ANOVAResult;
    const groupValueMap = buildGroupValueMapFromGrid(gridData, groupCol, valueCol);
    let oneWayMethod: OneWayAnovaMethodKind = 'standard';
    if (anovaType === 'repeatedMeasures') {
      oneWayMethod =
        analysisOptions.anovaRepeatedMethod === 'lmm' ? 'lmm' : 'repeated';
    } else if (result.varianceComponents) {
      oneWayMethod = 'lmm';
    } else if (result.ssSubject !== undefined) {
      oneWayMethod = 'repeated';
    }

    const walkthrough = buildOneWayAnovaWalkthrough({
      result,
      groupValueMap,
      t: (key, options) => t(key, options),
      oneWayMethod,
      postHoc: postHoc || undefined,
      controlGroupName: analysisOptions.anovaControlGroup as string | undefined,
    });

    return (
      <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
        <Title className="mb-2">{t('keisuke.anova.oneWay.flowTitle')}</Title>
        <pre className="bg-light p-3 rounded mb-3 small" style={{ whiteSpace: 'pre-wrap' }}>
          {walkthrough}
        </pre>
        {anovaPairwise && anovaPairwise.length > 0 ? (
          <>
            <div className="fw-semibold mb-2">{t('keisuke.anova.postHoc.comparison')}</div>
            <Table striped bordered size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>{t('keisuke.dashboard.anovaPairwiseGroupA')}</th>
                  <th>{t('keisuke.dashboard.anovaPairwiseGroupB')}</th>
                  <th>{t('keisuke.dashboard.anovaPairwisePValue')}</th>
                  <th>{t('keisuke.dashboard.anovaPairwiseSignificant')}</th>
                </tr>
              </thead>
              <tbody>
                {anovaPairwise.map((row, idx) => (
                  <tr key={`${row.groupA}-${row.groupB}-${idx}`}>
                    <td>{row.groupA}</td>
                    <td>{row.groupB}</td>
                    <td>{formatPValueCell(row.pValue)}</td>
                    <td>
                      {row.significant
                        ? pValueToSymbol(row.pValue) || '*'
                        : t('keisuke.dashboard.significanceNs')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        ) : null}
      </div>
    );
  }

  return null;
}
